import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card } from '../components/ui';
import { Plus, Trash2, Save, X, Building2 } from 'lucide-react';

const tomLinje = () => ({ beskrivelse: '', antall: 1, enhetspris: '', mva_sats: 25 });

const TilbudNy = () => {
  const nav = useNavigate();
  const [meta, setMeta] = useState({
    dato: new Date().toISOString().slice(0, 10),
    gyldig_til: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [kunde, setKunde] = useState({
    kunde_navn: '', kunde_adresse: '', kunde_postnr: '', kunde_sted: '',
    kunde_orgnr: '', kunde_epost: '', kunde_telefon: '', notater: '',
  });
  const [linjer, setLinjer] = useState([tomLinje()]);
  const [feil, setFeil] = useState('');
  const [busy, setBusy] = useState(false);
  const [kontakter, setKontakter] = useState([]);
  const [forslag, setForslag] = useState([]);
  const [visForslag, setVisForslag] = useState(false);
  const [aktivt, setAktivt] = useState(-1);
  const [valgtKontaktId, setValgtKontaktId] = useState(null);
  const [brregBusy, setBrregBusy] = useState(false);
  const [produkter, setProdukter] = useState([]);
  const [aktivLinje, setAktivLinje] = useState(-1);
  const [aktivPi, setAktivPi] = useState(-1);
  const sokRef = useRef();

  useEffect(() => {
    api.get('/kontakter?type=kunde').then((r) => setKontakter(r.data));
    api.get('/produkter').then((r) => setProdukter(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = (kunde.kunde_navn || '').trim();
    if (q.length < 2 || valgtKontaktId) { setForslag([]); return; }
    const lokale = kontakter
      .filter((k) => (k.navn || '').toLowerCase().includes(q.toLowerCase())
                  || (k.orgnr || '').includes(q))
      .slice(0, 5).map((k) => ({ kilde: 'kontakt', ...k }));
    setForslag(lokale);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/brreg/sok', { params: { q } });
        const fb = (r.data || []).slice(0, 8).map((e) => ({ kilde: 'brreg', ...e }));
        const finnes = new Set(lokale.map((l) => l.orgnr).filter(Boolean));
        setForslag([...lokale, ...fb.filter((b) => !finnes.has(b.orgnr))]);
      } catch { /* */ }
    }, 300);
    return () => clearTimeout(t);
  }, [kunde.kunde_navn, kontakter, valgtKontaktId]);

  const velgForslag = (f) => {
    setKunde({
      ...kunde,
      kunde_navn: f.navn || '', kunde_adresse: f.adresse || '',
      kunde_postnr: f.postnr || '', kunde_sted: f.sted || '',
      kunde_orgnr: f.orgnr || '',
      kunde_epost: f.epost || kunde.kunde_epost,
      kunde_telefon: f.telefon || kunde.kunde_telefon,
    });
    setValgtKontaktId(f.kilde === 'kontakt' ? f.id : null);
    setVisForslag(false); setForslag([]);
  };
  const slaaOppOrgnr = async () => {
    const orgnr = (kunde.kunde_orgnr || '').replace(/\s+/g, '');
    if (orgnr.length !== 9) { setFeil('Org.nr må være 9 siffer.'); return; }
    setBrregBusy(true); setFeil('');
    try {
      const r = await api.get(`/brreg/${orgnr}`);
      setKunde({ ...kunde, kunde_navn: r.data.navn || kunde.kunde_navn,
        kunde_adresse: r.data.adresse || '', kunde_postnr: r.data.postnr || '',
        kunde_sted: r.data.sted || '' });
    } catch (err) { setFeil(err.response?.data?.error || 'Brreg-oppslag feilet.'); }
    finally { setBrregBusy(false); }
  };
  const tasterINavn = (e) => {
    if (!visForslag || forslag.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktivt((i) => Math.min(forslag.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktivt((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' && aktivt >= 0) { e.preventDefault(); velgForslag(forslag[aktivt]); }
    else if (e.key === 'Escape') { setVisForslag(false); }
  };

  const oppdater = (i, key, val) =>
    setLinjer(linjer.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  const slett = (i) => setLinjer(linjer.filter((_, idx) => idx !== i));
  const leggTil = () => setLinjer([...linjer, tomLinje()]);

  const produktForslag = (q) => {
    const s = (q || '').trim().toLowerCase();
    if (s.length < 1) return [];
    return produkter.filter((p) => (p.navn || '').toLowerCase().includes(s)
                                 || (p.beskrivelse || '').toLowerCase().includes(s)).slice(0, 8);
  };
  const velgProdukt = (i, p) => {
    setLinjer(linjer.map((l, idx) => idx === i ? {
      ...l, beskrivelse: p.beskrivelse || p.navn || '',
      enhetspris: p.enhetspris != null ? p.enhetspris : l.enhetspris,
      mva_sats: p.mva_sats != null ? p.mva_sats : l.mva_sats,
    } : l));
    setAktivLinje(-1); setAktivPi(-1);
  };
  const tasterIBeskrivelse = (e, i) => {
    const liste = produktForslag(linjer[i].beskrivelse);
    if (aktivLinje !== i || liste.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktivPi((x) => Math.min(liste.length - 1, x + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktivPi((x) => Math.max(0, x - 1)); }
    else if (e.key === 'Enter' && aktivPi >= 0) { e.preventDefault(); velgProdukt(i, liste[aktivPi]); }
    else if (e.key === 'Escape') { setAktivLinje(-1); }
  };

  const beregn = (l) => {
    const a = parseFloat(String(l.antall).replace(',', '.')) || 0;
    const p = parseFloat(String(l.enhetspris).replace(',', '.')) || 0;
    const m = parseFloat(l.mva_sats) || 0;
    const eks = a * p; const mva = eks * m / 100;
    return { eks, mva, ink: eks + mva };
  };
  const totaler = linjer.reduce((acc, l) => {
    const { eks, mva, ink } = beregn(l);
    return { eks: acc.eks + eks, mva: acc.mva + mva, ink: acc.ink + ink };
  }, { eks: 0, mva: 0, ink: 0 });

  const lagre = async () => {
    setFeil(''); setBusy(true);
    try {
      const r = await api.post('/tilbud', { ...kunde, ...meta, linjer });
      nav(`/tilbud/${r.data.id}`);
    } catch (e) {
      setFeil(e.response?.data?.error || 'Klarte ikke lagre tilbud.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        title="Nytt tilbud"
        actions={<>
          <button className="btn-ghost" onClick={() => nav('/tilbud')}><X size={14} /> Avbryt</button>
          <button className="btn-primary" onClick={lagre} disabled={busy}><Save size={14} /> Lagre tilbud</button>
        </>}
      />
      {feil && <div className="badge badge-danger" style={{ marginBottom: 12, padding: 8 }}>{feil}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Card>
          <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600 }}>Kunde</h3>

          <label>Kundenavn *</label>
          <div style={{ position: 'relative', marginBottom: 8 }} ref={sokRef}>
            <input value={kunde.kunde_navn}
                   onChange={(e) => { setKunde({ ...kunde, kunde_navn: e.target.value }); setValgtKontaktId(null); setVisForslag(true); setAktivt(-1); }}
                   onFocus={() => setVisForslag(true)}
                   onBlur={() => setTimeout(() => setVisForslag(false), 180)}
                   onKeyDown={tasterINavn} placeholder="Begynn å skrive…" />
            {visForslag && forslag.length > 0 && (
              <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2, padding: 0, maxHeight: 280, overflow: 'auto' }}>
                {forslag.map((f, i) => (
                  <div key={`${f.kilde}-${f.id || f.orgnr || i}`}
                       onMouseDown={(e) => { e.preventDefault(); velgForslag(f); }}
                       onMouseEnter={() => setAktivt(i)}
                       style={{ padding: '8px 10px', cursor: 'pointer',
                                background: aktivt === i ? 'var(--theme-bg-hover, rgba(255,255,255,0.06))' : 'transparent',
                                borderTop: i ? '1px solid var(--theme-border)' : 'none' }}>
                    <div className="flex items-center justify-between" style={{ gap: 8 }}>
                      <strong style={{ fontSize: 12 }}>{f.navn}</strong>
                      <span className={`badge ${f.kilde === 'kontakt' ? 'badge-success' : 'badge-info'}`} style={{ fontSize: 9 }}>
                        {f.kilde === 'kontakt' ? 'Kontakt' : 'Brønnøysund'}
                      </span>
                    </div>
                    <div className="fm-text-muted" style={{ fontSize: 11 }}>
                      {f.orgnr && <>Org.nr {f.orgnr} · </>}
                      {[f.adresse, f.postnr, f.sted].filter(Boolean).join(' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label>Org.nr</label>
          <div className="flex" style={{ gap: 6, marginBottom: 8 }}>
            <input value={kunde.kunde_orgnr} onChange={(e) => setKunde({ ...kunde, kunde_orgnr: e.target.value })}
                   placeholder="9 siffer" style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" onClick={slaaOppOrgnr} disabled={brregBusy}>
              <Building2 size={12} /> {brregBusy ? '…' : 'Slå opp'}
            </button>
          </div>

          <label>Adresse</label>
          <input value={kunde.kunde_adresse} onChange={(e) => setKunde({ ...kunde, kunde_adresse: e.target.value })} style={{ marginBottom: 8 }} />

          <div className="grid" style={{ gridTemplateColumns: '90px 1fr', gap: 6, marginBottom: 8 }}>
            <div><label>Postnr</label><input value={kunde.kunde_postnr} onChange={(e) => setKunde({ ...kunde, kunde_postnr: e.target.value })} /></div>
            <div><label>Sted</label><input value={kunde.kunde_sted} onChange={(e) => setKunde({ ...kunde, kunde_sted: e.target.value })} /></div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
            <div><label>E-post</label><input value={kunde.kunde_epost} onChange={(e) => setKunde({ ...kunde, kunde_epost: e.target.value })} /></div>
            <div><label>Telefon</label><input value={kunde.kunde_telefon} onChange={(e) => setKunde({ ...kunde, kunde_telefon: e.target.value })} /></div>
          </div>
          <label>Notater</label>
          <textarea value={kunde.notater} onChange={(e) => setKunde({ ...kunde, notater: e.target.value })} />
        </Card>

        <Card>
          <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600 }}>Detaljer</h3>
          <label>Tilbudsdato</label>
          <input type="date" value={meta.dato} onChange={(e) => setMeta({ ...meta, dato: e.target.value })} style={{ marginBottom: 8 }} />
          <label>Gyldig til</label>
          <input type="date" value={meta.gyldig_til} onChange={(e) => setMeta({ ...meta, gyldig_til: e.target.value })} />
          <p className="fm-text-muted" style={{ fontSize: 11, marginTop: 8 }}>
            Tilbudsnummer (T-{new Date().getFullYear()}-NNNN) tildeles automatisk når du lagrer.
          </p>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Linjer</h3>
          <button className="btn-ghost" onClick={leggTil}><Plus size={12} /> Ny linje</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Beskrivelse</th>
              <th style={{ width: 70, textAlign: 'right' }}>Antall</th>
              <th style={{ width: 110, textAlign: 'right' }}>Pris</th>
              <th style={{ width: 70, textAlign: 'right' }}>MVA %</th>
              <th style={{ width: 110, textAlign: 'right' }}>Sum eks.</th>
              <th style={{ width: 110, textAlign: 'right' }}>Sum ink.</th>
              <th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {linjer.map((l, i) => {
              const { eks, ink } = beregn(l);
              const pliste = produktForslag(l.beskrivelse);
              const visListe = aktivLinje === i && pliste.length > 0;
              return (
                <tr key={i}>
                  <td style={{ position: 'relative' }}>
                    <input value={l.beskrivelse}
                           onChange={(e) => { oppdater(i, 'beskrivelse', e.target.value); setAktivLinje(i); setAktivPi(-1); }}
                           onFocus={() => setAktivLinje(i)}
                           onBlur={() => setTimeout(() => setAktivLinje((x) => x === i ? -1 : x), 180)}
                           onKeyDown={(e) => tasterIBeskrivelse(e, i)}
                           placeholder="Søk i produkter / fritekst…" />
                    {visListe && (
                      <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2, padding: 0, maxHeight: 240, overflow: 'auto' }}>
                        {pliste.map((p, pi) => (
                          <div key={p.id} onMouseDown={(e) => { e.preventDefault(); velgProdukt(i, p); }}
                               onMouseEnter={() => setAktivPi(pi)}
                               style={{ padding: '6px 10px', cursor: 'pointer',
                                        background: aktivPi === pi ? 'var(--theme-bg-hover, rgba(255,255,255,0.06))' : 'transparent',
                                        borderTop: pi ? '1px solid var(--theme-border)' : 'none' }}>
                            <div className="flex items-center justify-between" style={{ gap: 8 }}>
                              <strong style={{ fontSize: 12 }}>{p.navn}</strong>
                              <span className="fm-text-muted" style={{ fontSize: 11 }}>{kr(p.enhetspris || 0)} · {p.mva_sats}%</span>
                            </div>
                            {p.beskrivelse && p.beskrivelse !== p.navn && (
                              <div className="fm-text-muted" style={{ fontSize: 11 }}>{p.beskrivelse}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td><input value={l.antall} onChange={(e) => oppdater(i, 'antall', e.target.value)} style={{ textAlign: 'right' }} /></td>
                  <td><input value={l.enhetspris} onChange={(e) => oppdater(i, 'enhetspris', e.target.value)} style={{ textAlign: 'right' }} /></td>
                  <td>
                    <select value={l.mva_sats} onChange={(e) => oppdater(i, 'mva_sats', parseFloat(e.target.value))}>
                      <option value={25}>25</option><option value={15}>15</option>
                      <option value={12}>12</option><option value={0}>0</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>{kr(eks)}</td>
                  <td style={{ textAlign: 'right' }}>{kr(ink)}</td>
                  <td>
                    <button className="btn-ghost" onClick={() => slett(i)} disabled={linjer.length === 1}
                            style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, padding: '10px 12px' }}>Sum eks. MVA</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{kr(totaler.eks)}</td><td colSpan={2}></td></tr>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, padding: '4px 12px' }}>MVA</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{kr(totaler.mva)}</td><td colSpan={2}></td></tr>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, padding: '6px 12px', fontSize: 13 }}>Total</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--theme-accent)', fontSize: 13 }}>{kr(totaler.ink)}</td><td colSpan={2}></td></tr>
          </tfoot>
        </table>
      </Card>
    </>
  );
};

export default TilbudNy;
